/**
 * Billing → **Plan** (plan-020 **WAPP-BILL**; design §32.12.3 Q-23, Q-24, §32.17 "Billing → Plan section"; R-APP-1).
 *
 * The organization's plan where its money is managed: the plan's name and state, this period, the included minutes
 * used and left (from seconds, never rounded — OA-1), the extra minutes and what they cost so far (as of the last day
 * close, `extra_as_of`), whether extra time is on (the switch itself is the Extra time card beside the rates —
 * unchanged), the next charge (net + VAT = total), and the card with how it is charged. On pay as you go it says so and
 * offers the plans.
 *
 * The acts need `account.finance.manage`: "Pay now" while a renewal waits for the person (the processor's page, through
 * the pay operation), "Replace card" (Billing's dialog), "Change plan" (Finance → Plans) and "Cancel plan" (Q-21's
 * dialog, here, because Q-13 tells the person to cancel "in Billing > Plan"), or "Keep plan" while a cancellation is
 * scheduled. A finance reader without manage sees the facts and "Change plan" (the plans are readable to them).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import type { BalanceSubscriptionView } from "../../api/slice/subscription.js";
import { resolvePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
import { formatMoney } from "../../money.js";
import type { Outcome } from "../account-admin/page/organization-page.js";
import { longDateText, talkTimeText, vatTextOf } from "../finance/plans/plan-copy.js";
import { CancelDialog, cardStateText, heldNameOf, heldStateText } from "../finance/plans/held-plan.js";
import { RefusalLine } from "../finance/plans/plan-quote.js";
import { renewalUnpaid, useSharedSubscription, usePayOperation } from "./plan-billing.js";

export interface PlanSectionProps {
  /** Opens Billing's card replacement dialog. */
  readonly onReplaceCard: () => void;
  /** Opens the pay page for an operation (when the processor gives no page to go to). */
  readonly onPayPage: (operationId: string) => void;
}

function PlanFacts({ held }: { readonly held: BalanceSubscriptionView }): ReactNode {
  usePortalTranslation("subscription");
  const zone = held.billingTimezone;
  const next = held.nextCharge;
  const cardState = cardStateText(held);
  return (
    <dl className="bl-plan-facts">
      {held.period === null ? null : (
        <div className="bl-plan-fact">
          <dd>{portalText("subscription", "held.this_period", { start: longDateText(held.period.start, zone), end: longDateText(held.period.end, zone) })}</dd>
        </div>
      )}
      <div className="bl-plan-fact">
        <dd>{portalText("subscription", "held.included", { used: talkTimeText(held.includedTalkSecondUsed), left: talkTimeText(held.includedTalkSecondLeft) })}</dd>
      </div>
      <div className="bl-plan-fact">
        <dd>{portalText("subscription", "held.extra", { extra_minutes: talkTimeText(held.extraTalkSecond), extra_amount: formatMoney(held.extraAmount) })}</dd>
        <dd className="bl-plan-note">{portalText("subscription", "held.extra_as_of")}</dd>
      </div>
      <div className="bl-plan-fact">
        <dd>{held.extraTimeEnabled ? portalText("subscription", "held.extra_time_on") : portalText("subscription", "held.extra_time_off")}</dd>
        <dd className="bl-plan-note">{portalText("billing", "plan.extra_time_where")}</dd>
      </div>
      {next === null ? null : (
        <div className="bl-plan-fact">
          <dd>{portalText("subscription", "held.next_charge", { total: formatMoney(next.total), net: formatMoney(next.net), vat_text: vatTextOf(next.vat, null, null), date: longDateText(next.at, zone) })}</dd>
        </div>
      )}
      <div className="bl-plan-fact">
        <dd>
          {held.card === null || held.card.lastFour === null ? portalText("subscription", "held.card_none") : portalText("subscription", "held.card", { last4: held.card.lastFour })}
          {cardState === null ? null : <span className="bl-plan-note"> · {cardState}</span>}
        </dd>
      </div>
    </dl>
  );
}

export function PlanSection(props: PlanSectionProps): ReactNode {
  usePortalTranslation("billing");
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const shared = useSharedSubscription();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const pay = usePayOperation((view) => props.onPayPage(view.operationId));
  const withdrawing = useMutation({
    mutationFn: (billingSubscriptionId: string) => gateway.withdrawSubscriptionCancellation(billingSubscriptionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["balance-subscription"] });
      setOutcome({ tone: "done", text: () => portalText("subscription", "cancel.withdrawn") });
    },
  });

  if (!shared.canRead) return null;
  const held = shared.held;
  return (
    <section className="bl-card bl-plan" aria-labelledby="bl-plan-title">
      <p className="bl-card-kicker" id="bl-plan-title">{portalText("billing", "plan.kicker")}</p>
      {shared.failed ? (
        <p className="bl-card-line" role="alert">
          {portalText("billing", "plan.unreadable")}
        </p>
      ) : held === undefined ? (
        <p className="bl-card-line" aria-busy="true">{portalText("subscription", "quote.loading")}</p>
      ) : held === null ? (
        <>
          <p className="bl-plan-name">{portalText("billing", "plan.payg")}</p>
          <p className="bl-card-line">{portalText("billing", "plan.payg_line")}</p>
          <div className="bl-actions">
            <Link to="/finance/plans" className="btn btn-secondary">{portalText("billing", "plan.change")}</Link>
          </div>
        </>
      ) : (
        <>
          <p className="bl-plan-name">
            <span>{heldNameOf(held)}</span>
            <Tag status={held.state === "suspended" ? "critical" : held.state === "past_due" ? "caution" : "positive"}>{heldStateText(held.state)}</Tag>
            {held.cancelAtPeriodEnd && held.period !== null ? (
              <Tag tone="neutral">{portalText("subscription", "held.ends", { period_end: longDateText(held.period.end, held.billingTimezone) })}</Tag>
            ) : null}
          </p>
          {held.state === "pending_activation" ? <p className="bl-card-line">{portalText("subscription", "held.pending")}</p> : null}
          <PlanFacts held={held} />
          {outcome === null ? null : (
            <p className={outcome.tone === "done" ? "bl-card-line" : "bl-plan-error"} role="status">
              {resolvePortalMessage(outcome.text)}
            </p>
          )}
          {pay.isError ? <RefusalLine error={pay.error} otherwise={portalText("finance", "m0615")} /> : null}
          {withdrawing.isError ? <RefusalLine error={withdrawing.error} otherwise={portalText("finance", "m0694")} /> : null}
          <div className="bl-actions">
            {shared.canManage && renewalUnpaid(held) && held.payOperationId !== null ? (
              <Button variant="primary" disabled={pay.isPending} onClick={() => pay.mutate(held.payOperationId ?? "")}>
                {portalText("billing", "plan.pay_now")}
              </Button>
            ) : null}
            {shared.canManage && held.state !== "pending_activation" ? (
              <Button variant={renewalUnpaid(held) ? "secondary" : "ghost"} onClick={props.onReplaceCard}>
                {portalText("billing", "plan.replace_card")}
              </Button>
            ) : null}
            <Link to="/finance/plans" className="btn btn-secondary">{portalText("billing", "plan.change")}</Link>
            {shared.canManage && held.cancelAtPeriodEnd ? (
              <Button variant="ghost" disabled={withdrawing.isPending} onClick={() => withdrawing.mutate(held.billingSubscriptionId)}>
                {portalText("subscription", "held.keep")}
              </Button>
            ) : null}
            {shared.canManage && !held.cancelAtPeriodEnd && held.state !== "pending_activation" ? (
              <Button variant="ghost" onClick={() => setCancelOpen(true)}>
                {portalText("billing", "plan.cancel")}
              </Button>
            ) : null}
          </div>
          {shared.canManage && shared.accountId !== null ? (
            <CancelDialog open={cancelOpen} accountId={shared.accountId} held={held} onClose={() => setCancelOpen(false)} onDone={setOutcome} />
          ) : null}
        </>
      )}
    </section>
  );
}
